import { memo, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { RANK_LABELS, RANK_NAMES, SUIT_NAMES, type Card } from "../../game";

const SUIT_SYMBOL: Record<Card["suit"], string> = {
  hearts: "♥",
  diamonds: "♦",
  clubs: "♣",
  spades: "♠",
};

interface PlayingCardProps {
  card: Card;
  size?: "rack" | "center" | "mini";
  interactive?: boolean;
  pending?: boolean;
  playablePiles?: Array<0 | 1>;
  onPlay?: (pileIndex: 0 | 1) => void;
  onInvalid?: () => void;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;
}

function resetDrag(element: HTMLDivElement): void {
  element.classList.remove("is-dragging");
  element.style.removeProperty("--drag-x");
  element.style.removeProperty("--drag-y");
}

export const PlayingCard = memo(function PlayingCard({
  card,
  size = "rack",
  interactive = false,
  pending = false,
  playablePiles = [],
  onPlay,
  onInvalid,
}: PlayingCardProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const playable = interactive && playablePiles.length > 0 && !pending;
  const red = card.suit === "hearts" || card.suit === "diamonds";
  const label = `${RANK_NAMES[card.rank]} de ${SUIT_NAMES[card.suit]}`;

  function nearestPile(element: HTMLDivElement): 0 | 1 {
    if (playablePiles.length === 1) return playablePiles[0];
    const cardCenter = element.getBoundingClientRect().left + element.offsetWidth / 2;
    const distances = playablePiles.map((index) => {
      const target = document.querySelector<HTMLElement>(`[data-pile-index="${index}"]`);
      if (!target) return { index, distance: Number.POSITIVE_INFINITY };
      const rect = target.getBoundingClientRect();
      return { index, distance: Math.abs(cardCenter - (rect.left + rect.width / 2)) };
    });
    distances.sort((first, second) => first.distance - second.distance);
    return distances[0].index;
  }

  function pileAtPoint(x: number, y: number): 0 | 1 | null {
    for (const index of playablePiles) {
      const target = document.querySelector<HTMLElement>(`[data-pile-index="${index}"]`);
      if (!target) continue;
      const rect = target.getBoundingClientRect();
      const padding = 18;
      if (
        x >= rect.left - padding &&
        x <= rect.right + padding &&
        y >= rect.top - padding &&
        y <= rect.bottom + padding
      ) {
        return index;
      }
    }
    return null;
  }

  function activate(): void {
    const element = elementRef.current;
    if (!element || pending) return;
    if (!playable) {
      onInvalid?.();
      element.animate(
        [
          { transform: "translateX(0)" },
          { transform: "translateX(-3px)" },
          { transform: "translateX(3px)" },
          { transform: "translateX(0)" },
        ],
        { duration: 140 },
      );
      return;
    }
    onPlay?.(nearestPile(element));
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (!interactive || pending || event.button !== 0) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.classList.add("is-pressed");
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const x = event.clientX - drag.startX;
    const y = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(x, y) > 7) drag.moved = true;
    if (!drag.moved) return;

    event.currentTarget.classList.remove("is-pressed");
    event.currentTarget.classList.add("is-dragging");
    event.currentTarget.style.setProperty("--drag-x", `${x}px`);
    event.currentTarget.style.setProperty("--drag-y", `${y}px`);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.classList.remove("is-pressed");
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const targetPile = drag.moved ? pileAtPoint(event.clientX, event.clientY) : null;
    resetDrag(event.currentTarget);

    if (drag.moved) {
      if (targetPile !== null) onPlay?.(targetPile);
      else onInvalid?.();
      return;
    }
    activate();
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLDivElement>): void {
    dragRef.current = null;
    event.currentTarget.classList.remove("is-pressed");
    resetDrag(event.currentTarget);
  }

  return (
    <div
      ref={elementRef}
      className={`playing-card playing-card--${size} ${red ? "is-red" : "is-black"} ${pending ? "is-pending" : ""}`}
      data-playable={playable ? "true" : "false"}
      role={interactive ? "button" : "img"}
      tabIndex={playable ? 0 : -1}
      aria-label={`${label}${playable ? ", jugable" : ""}`}
      aria-disabled={interactive ? !playable || pending : undefined}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate();
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <span className="playing-card__corner" aria-hidden="true">
        <strong>{RANK_LABELS[card.rank]}</strong>
        <span>{SUIT_SYMBOL[card.suit]}</span>
      </span>
      <span className="playing-card__center" aria-hidden="true">
        <span className="playing-card__pip">{SUIT_SYMBOL[card.suit]}</span>
        {card.rank >= 11 ? <strong className="playing-card__face"><span>{RANK_LABELS[card.rank]}</span></strong> : null}
      </span>
      <span className="playing-card__corner playing-card__corner--bottom" aria-hidden="true">
        <strong>{RANK_LABELS[card.rank]}</strong>
        <span>{SUIT_SYMBOL[card.suit]}</span>
      </span>
    </div>
  );
});

export function CardBack({ label = "Mazo", compact = false }: { label?: string; compact?: boolean }) {
  return (
    <div className={`card-back ${compact ? "card-back--compact" : ""}`} role="img" aria-label={label}>
      <svg viewBox="0 0 80 112" aria-hidden="true" focusable="false">
        <rect x="3" y="3" width="74" height="106" rx="8" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 24 40 8l28 16-28 16L12 24Zm0 32 28-16 28 16-28 16-28-16Zm0 32 28-16 28 16-28 16-28-16Z" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="40" cy="56" r="11" fill="currentColor" />
        <path d="m34 56 4 4 9-10" fill="none" stroke="var(--felt)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
